import type { ScopeNode } from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import type { TreeNode } from "../../../../Components/Tree/types";
import type { AffectedHierarchy, ScopeNodeType } from "./types";

export interface AffectedHierarchyView {
  // A single chain: the target's renderable ancestors down to the target and its subtree.
  nodes: TreeNode[];
  inUseIds: Set<string>;
}

const RENDERABLE_TYPES: readonly ScopeNodeType[] = ["scope", "group"];

const isRenderable = (node: ScopeNode): boolean =>
  (RENDERABLE_TYPES as readonly string[]).includes(node.type);

const findPath = (
  node: ScopeNode,
  targetId: string,
  ancestors: ScopeNode[]
): ScopeNode[] | null => {
  const path = [...ancestors, node];
  if (String(node.id) === targetId) {
    return path;
  }
  for (const child of node.children ?? []) {
    const found = findPath(child, targetId, path);
    if (found) {
      return found;
    }
  }
  return null;
};

// A non-renderable node is skipped and its children hoisted in its place.
const pruneChildren = (node: ScopeNode): TreeNode[] =>
  (node.children ?? []).flatMap((child) =>
    isRenderable(child)
      ? [{ ...child, children: pruneChildren(child) }]
      : pruneChildren(child)
  );

export const buildAffectedHierarchy = ({
  tree,
  targetId,
  descendantsInUse,
  targetInUse = false,
}: AffectedHierarchy): AffectedHierarchyView | null => {
  const roots = Array.isArray(tree) ? tree : [tree];
  const id = String(targetId);

  let path: ScopeNode[] | null = null;
  for (const root of roots) {
    path = findPath(root, id, []);
    if (path) {
      break;
    }
  }
  if (!path) {
    return null;
  }

  const chain = path.filter(isRenderable);
  const target = chain[chain.length - 1];
  if (!target || String(target.id) !== id) {
    return null;
  }

  let node: TreeNode = { ...target, children: pruneChildren(target) };
  for (let i = chain.length - 2; i >= 0; i -= 1) {
    node = { ...chain[i], children: [node] };
  }

  const inUseIds = new Set(descendantsInUse.map((ref) => String(ref.id)));
  if (targetInUse) {
    inUseIds.add(id);
  }
  return { nodes: [node], inUseIds };
};

export default buildAffectedHierarchy;
